import { updateDerivedFields as updateDerivedFieldsPartLib } from "../PartLib/utils";
import {
  computeCommonWeldHour,
  compWeldHourComputeMap,
} from "./computeWeldHour";
import { ContactSupport } from "@mui/icons-material";
import { transformComponentName } from "../PartLib/utils";
import { getPipingTable } from "../sqliteApi";

export const UNITS_ENUM = {
  Ft: "ft",
  M: "m",
  Ft2: "ft²",
  m2: "m²",
  no: "Nos",
};

export const UNITS = Object.values(UNITS_ENUM);

// ─── Components that use length-based units (Ft/M) instead of Nos ───────────
export const LENGTH_BASED_COMPONENTS = ["Pipe", "Nipple", "Tube"];

export function isLengthBased(componentName) {
  if (!componentName) return false;
  const lower = componentName.toLowerCase();
  return LENGTH_BASED_COMPONENTS.some((c) => lower.includes(c.toLowerCase()));
}

export function getUnitForComponent(componentName, isMetric) {
  return isLengthBased(componentName) ? (isMetric ? "m" : "ft") : "Nos";
}

export function round2(v) {
  return Math.round(v * 100) / 100;
}

export function resetDerivedFields(handleFieldUpdate, rowIndex) {
  handleFieldUpdate(rowIndex, "Weight", "N/A");

  handleFieldUpdate(rowIndex, "WeldHour", "N/A");
  handleFieldUpdate(rowIndex, "BoltupHour", "N/A");

  handleFieldUpdate(rowIndex, "TotalWeight", 0);
  handleFieldUpdate(rowIndex, "TotalFabHours", 0);
}

export async function updateDerivedFields(
  filteredSelectedSpecs,
  handleFieldUpdate,
  rowIndex,
  rowData,
) {
  await updateDerivedFieldsPartLib(
    filteredSelectedSpecs,
    handleFieldUpdate,
    rowIndex,
    rowData,
  );
}

function pickSmallestSize(size1, size2) {
  const toNum = (s) => {
    const t = String(s ?? "").trim();
    if (!t) return NaN;

    // "1 1/2"
    if (t.includes(" ")) {
      const [w, frac] = t.split(/\s+/);
      const [n, d] = String(frac).split("/");
      const whole = parseFloat(w);
      const num = parseFloat(n);
      const den = parseFloat(d);
      if (isFinite(whole) && isFinite(num) && isFinite(den) && den !== 0) {
        return whole + num / den;
      }
    }

    // "1/2"
    if (t.includes("/")) {
      const [n, d] = t.split("/");
      const num = parseFloat(n);
      const den = parseFloat(d);
      if (isFinite(num) && isFinite(den) && den !== 0) return num / den;
    }

    // "2" , "2.5"
    const v = parseFloat(t);
    return isFinite(v) ? v : NaN;
  };

  const s1 = String(size1 ?? "").trim();
  const s2 = String(size2 ?? "").trim();

  const n1 = toNum(s1);
  const n2 = toNum(s2);

  if (isFinite(n1) && isFinite(n2)) return n1 <= n2 ? s1 : s2;
  return s1 || s2;
}

async function getBoltupHours(boltupHoursValue, Size, Schedule, rowIndex) {
  const norm = (v) => String(v ?? "").trim();

  if (
    boltupHoursValue === null ||
    boltupHoursValue === undefined ||
    boltupHoursValue === ""
  ) {
    return 0;
  }

  const parsedValue = parseFloat(boltupHoursValue);

  if (!isNaN(parsedValue) && isFinite(parsedValue)) {
    return parsedValue;
  }

  if (
    typeof boltupHoursValue === "string" &&
    boltupHoursValue.includes("Bolt-ups Time")
  ) {
    const boltupTimeTable = await getPipingTable("Bolt-ups Time");

    if (!boltupTimeTable || !boltupTimeTable.length) {
      return 0;
    }

    const boltupRow = boltupTimeTable.find(
      (row) => norm(row.Size) === norm(Size),
    );

    if (!boltupRow) {
      return "N/A";
    }

    let scheduleValue = boltupRow[Schedule];

    if (scheduleValue === undefined || scheduleValue === null) {
      const scheduleMap = {
        STD: "STD",
        Standard: "STD",
        40: "40",
        "Sch 40": "40",
        80: "80",
        "Sch 80": "80",
        150: "150",
        160: "160",
        XH: "XH",
        "Extra Heavy": "XH",
        XXH: "XXH",
        "Double Extra Heavy": "XXH",
      };

      const mapped = scheduleMap[Schedule];
      if (mapped) {
        scheduleValue = boltupRow[mapped];
      }
    }

    if (scheduleValue !== undefined && scheduleValue !== null) {
      const value = parseFloat(scheduleValue);
      if (!isNaN(value)) {
        return value;
      }
    }

    return "N/A";
  }

  return "N/A";
}

async function getWeldHours(weldHoursValue, Size, Schedule, rowIndex) {
  const norm = (v) => String(v ?? "").trim();

  if (
    weldHoursValue === null ||
    weldHoursValue === undefined ||
    weldHoursValue === ""
  ) {
    return 0;
  }

  const parsedValue = parseFloat(weldHoursValue);

  if (!isNaN(parsedValue) && isFinite(parsedValue)) {
    return parsedValue;
  }

  if (
    typeof weldHoursValue === "string" &&
    weldHoursValue.includes("Manual Butt Welds")
  ) {
    const manualButtWeldsTable = await getPipingTable("Manual Butt Welds");

    if (!manualButtWeldsTable || !manualButtWeldsTable.length) {
      return "N/A";
    }

    const manualRow = manualButtWeldsTable.find(
      (row) => norm(row.Size) === norm(Size),
    );

    if (!manualRow) {
      return "N/A";
    }

    let scheduleValue = manualRow[Schedule];

    if (scheduleValue === undefined || scheduleValue === null) {
      const scheduleMap = {
        STD: "STD",
        Standard: "STD",
        40: "40",
        "Sch 40": "40",
        80: "80",
        "Sch 80": "80",
        150: "150",
        160: "160",
        XH: "XH",
        "Extra Heavy": "XH",
        XXH: "XXH",
        "Double Extra Heavy": "XXH",
      };

      const mapped = scheduleMap[Schedule];
      if (mapped) {
        scheduleValue = manualRow[mapped];
      }
    }

    if (scheduleValue !== undefined && scheduleValue !== null) {
      const value = parseFloat(scheduleValue);
      if (!isNaN(value)) {
        return value;
      }
    }

    return "N/A";
  }

  return "N/A";
}

async function getOletWeldHours(
  weldHoursValue,
  DualSize,
  Schedule,
  rowIndex,
) {
  const norm = (v) => String(v ?? "").trim();

  const dual = String(DualSize ?? "").trim();
  const [Size1 = "", Size2 = ""] = dual
    .split(/\s*[xX]\s*/)
    .map((v) => v.trim());
  const Size = pickSmallestSize(Size1, Size2);

  if (
    weldHoursValue === null ||
    weldHoursValue === undefined ||
    weldHoursValue === ""
  ) {
    return 0;
  }

  const parsedValue = parseFloat(weldHoursValue);
  if (!isNaN(parsedValue) && isFinite(parsedValue)) {
    return parsedValue;
  }

  if (
    typeof weldHoursValue === "string" &&
    weldHoursValue.includes("TOL/WOL's")
  ) {
    const tolWolTable = await getPipingTable("TOL/WOL's");

    if (!tolWolTable || !tolWolTable.length) {
      return "N/A";
    }

    const oletRow = tolWolTable.find((row) => norm(row.Size) === norm(Size));

    if (!oletRow) {
      return "N/A";
    }

    let scheduleValue = oletRow[Schedule];

    if (scheduleValue === undefined || scheduleValue === null) {
      const scheduleMap = {
        STD: "STD",
        Standard: "STD",
        40: "40",
        "Sch 40": "40",
        80: "80",
        "Sch 80": "80",
        150: "150",
        160: "160",
        XH: "XH",
        "Extra Heavy": "XH",
        XXH: "XXH",
        "Double Extra Heavy": "XXH",
      };

      const mapped = scheduleMap[Schedule];
      if (mapped) {
        scheduleValue = oletRow[mapped];
      }
    }

    if (scheduleValue !== undefined && scheduleValue !== null) {
      const value = parseFloat(scheduleValue);
      if (!isNaN(value)) {
        return value;
      }
    }

    return "N/A";
  }

  return "N/A";
}

async function getRedWeldHours(
  weldHoursValue,
  DualSize,
  Schedule,
  rowIndex,
) {
  const norm = (v) => String(v ?? "").trim();

  if (
    weldHoursValue === null ||
    weldHoursValue === undefined ||
    weldHoursValue === ""
  ) {
    return 0;
  }

  const parsedValue = parseFloat(weldHoursValue);
  if (!isNaN(parsedValue) && isFinite(parsedValue)) {
    return parsedValue;
  }

  if (
    typeof weldHoursValue === "string" &&
    weldHoursValue.includes("Manual Butt Welds Red")
  ) {
    const parts = DualSize?.split(" X ");

    if (!parts || parts.length < 2) {
      return "N/A";
    }

    const Size = norm(parts[0]);
    const ND2 = norm(parts[1]);

    const redTable = await getPipingTable("Manual Butt Welds Red");

    if (!redTable || !redTable.length) {
      return "N/A";
    }

    const redRow = redTable.find(
      (row) => norm(row.Size) === Size && norm(row.ND2) === ND2,
    );

    if (!redRow) {
      return "N/A";
    }

    let scheduleValue = redRow[Schedule];

    if (scheduleValue === undefined || scheduleValue === null) {
      const scheduleMap = {
        STD: "STD",
        Standard: "STD",
        40: "40",
        "Sch 40": "40",
        80: "80",
        "Sch 80": "80",
        150: "150",
        160: "160",
        XH: "XH",
        "Extra Heavy": "XH",
        XXH: "XXH",
        "Double Extra Heavy": "XXH",
      };

      const mapped = scheduleMap[Schedule];
      if (mapped) {
        scheduleValue = redRow[mapped];
      }
    }

    if (scheduleValue !== undefined && scheduleValue !== null) {
      const value = parseFloat(scheduleValue);
      if (!isNaN(value)) {
        return value;
      }
    }

    return "N/A";
  }

  return "N/A";
}

export async function estimate(
  estimationTargetTable,
  handleFieldUpdate,
  fittingsWeld,
  pipeWeld,
  changedRowIndices = null,
) {
  const rowsToProcess =
  changedRowIndices === null || changedRowIndices === undefined
    ? estimationTargetTable.map((_, index) => index)
    : Array.isArray(changedRowIndices)
      ? changedRowIndices
      : typeof changedRowIndices?.[Symbol.iterator] === "function"
        ? Array.from(changedRowIndices)
        : [changedRowIndices];

for (const i of rowsToProcess) {
    const row = estimationTargetTable[i];

    if (!row) continue;

    const { Qty, Components, Size, Ends, Spec } = row;
    const Schedule = row["Schedule/Rating"];
    const StockCode = row["StockCode"];

    const pipingFamiliesTableName = Spec ? `piping_spec_${Spec}` : null;

    if (!pipingFamiliesTableName) {
      resetDerivedFields(handleFieldUpdate, i);
      continue;
    }

    const pipingFamiliesTable = await getPipingTable(pipingFamiliesTableName);

    if (!pipingFamiliesTable || !pipingFamiliesTable.length) {
      resetDerivedFields(handleFieldUpdate, i);
      continue;
    }

    const familyRow = pipingFamiliesTable.find(
      (cur) => cur.Components === Components,
    );

    if (!familyRow) {
      resetDerivedFields(handleFieldUpdate, i);
      continue;
    }

    const weightTableName = familyRow.Est_Table_Name;
console.log("Weight Table Name:", weightTableName);
    if (!weightTableName) {
      resetDerivedFields(handleFieldUpdate, i);
      continue;
    }

    const targetTable = await getPipingTable(weightTableName);
    if (!weightTableName) {
      continue;
    }

    if (!targetTable) {
      continue;
    }

    if (!targetTable.length) {
      continue;
    }

    const hasTypeColumn =
      Array.isArray(targetTable) &&
      targetTable.length > 0 &&
      Object.prototype.hasOwnProperty.call(targetTable[0], "TYPE");

    const norm = (v) => String(v ?? "").trim();

    let targetRow = null;
    let matchLevel = null;

    targetRow = targetTable.find((cur) => {
      const sizeMatch = norm(cur.SIZE) === norm(Size);
      const schMatch = norm(cur.SCH_RATING) === norm(Schedule);

      if (!(sizeMatch && schMatch)) return false;

      if (hasTypeColumn) {
        const typeVal = norm(cur.TYPE);

        if (typeVal === "-" || typeVal === "") return true;

        return typeVal === norm(Ends);
      }

      return true;
    });

    if (targetRow) {
      matchLevel = "exact";
    } else {
      targetRow = targetTable.find(
        (cur) =>
          norm(cur.SIZE) === norm(Size) &&
          norm(cur.SCH_RATING) === norm(Schedule),
      );

      if (targetRow) {
        matchLevel = "sch";
      } else {
        targetRow = targetTable.find((cur) => norm(cur.SIZE) === norm(Size));

        if (targetRow) matchLevel = "size";
      }
    }
console.log("Target Row",targetRow);
    let weight;
    let unitCost;
    let rawWeldHours;
    let boltHour;

    if (!targetRow || targetRow === null || targetRow === undefined) {
      weight = "N/A";
      unitCost = "N/A";
      rawWeldHours = "N/A";
      boltHour = "N/A";
    } else {
      weight = targetRow?.["Unit_Weight"];
      unitCost = targetRow?.["Unit_Cost"];

      const weldHoursValue =
        targetRow?.["Weld Hours"] ?? targetRow?.["Weld_Hours"];

      if (Components?.toLowerCase().includes("olet")) {
        rawWeldHours = await getOletWeldHours(
          weldHoursValue,
          Size,
          Schedule,
          i,
        );
      } else if (Components?.toLowerCase().includes("red")) {
        rawWeldHours = await getRedWeldHours(
          weldHoursValue,
          Size,
          Schedule,
          i,
        );
      } else {
        rawWeldHours = await getWeldHours(
          weldHoursValue,
          Size,
          Schedule,
          i,
        );
      }

      const boltupHoursValue =
        targetRow?.["Boltup_Hours"] ?? targetRow?.["Boltup Hours"];

      boltHour = await getBoltupHours(boltupHoursValue, Size, Schedule, i);
    }

    if (weight === undefined || weight === null) weight = "N/A";
    if (unitCost === undefined || unitCost === null) unitCost = "N/A";
    if (boltHour === undefined || boltHour === null) boltHour = "N/A";
    if (rawWeldHours === undefined || rawWeldHours === null) {
      rawWeldHours = "N/A";
    }

    handleFieldUpdate(i, "Weight", weight);

    let finalWeldHour;

    if (rawWeldHours === "N/A") {
      finalWeldHour = "N/A";
    } else if (typeof rawWeldHours === "number" && Qty) {
      finalWeldHour = round2(rawWeldHours * (Number(Qty) || 0));
    } else if (rawWeldHours === 0 || rawWeldHours === "0") {
      finalWeldHour = 0;
    } else {
      finalWeldHour = "N/A";
    }

    handleFieldUpdate(i, "WeldHour", finalWeldHour);

    let finalBoltupHour;

    if (boltHour === "N/A") {
      finalBoltupHour = "N/A";
    } else if (typeof boltHour === "number" && Qty) {
      finalBoltupHour = round2(boltHour * (Number(Qty) || 0));
    } else if (boltHour === 0 || boltHour === "0") {
      finalBoltupHour = 0;
    } else {
      finalBoltupHour = "N/A";
    }

    handleFieldUpdate(i, "BoltupHour", finalBoltupHour);

    let totalWeight;

    if (weight === "N/A") {
      totalWeight = "N/A";
    } else if (typeof weight === "number" && Qty) {
      totalWeight = round2(weight * (Number(Qty) || 0));
    } else {
      totalWeight = "N/A";
    }

    handleFieldUpdate(i, "TotalWeight", totalWeight);

    let totalFabHours;

    if (finalWeldHour === "N/A" && finalBoltupHour === "N/A") {
      totalFabHours = "N/A";
    } else if (finalWeldHour === 0 && finalBoltupHour === 0) {
      totalFabHours = 0;
    } else if (finalWeldHour === 0 && finalBoltupHour === "N/A") {
      totalFabHours = 0;
    } else if (finalWeldHour === "N/A" && finalBoltupHour === 0) {
      totalFabHours = 0;
    } else {
      const weldNum = Number(finalWeldHour) || 0;
      const boltNum = Number(finalBoltupHour) || 0;

      totalFabHours = round2(weldNum + boltNum);
    }

    handleFieldUpdate(i, "TotalFabHours", totalFabHours);

    handleFieldUpdate(i, "MatchLevel", matchLevel);
  }
}